import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';

import { EntityTypes } from 'constants/entities';
import { IGet } from 'core/actions/admin/data';
import { createRequest, getRequest, updateRequest } from 'core/services/admin';
import { ILanguage, ITranslation } from 'types/domain/text';

/**
 * Which language the module editor is writing in.
 *
 * A module has one set of slides and a text layer per language. Picking a
 * language reloads the module with that layer laid over its text, and every
 * text save made on it lands in that layer rather than the base text — the
 * language travels on the text itself (`IText.languageId`), so this only has
 * to say which layer to load next.
 */
export interface ITranslationState {
  /** The language being edited; null for the module's base language. */
  languageId: number | null;
  /** Every language a module can be translated into, once `get LANG` has landed. */
  languages: ILanguage[] | null;
  /** The last text saved into a translation, for the confirmation it gets. */
  saved: { textId: number; languageId: number; at: number } | null;
  /**
   * The language a learner link asked for, by ISO code (`/fr/{token}`); null
   * for the base language. The player loads each module in it where one has
   * been translated, and in its base language where not.
   */
  learnerLanguage: string | null;
}

const initialState: ITranslationState = { languageId: null, languages: null, saved: null, learnerLanguage: null };

export const translationSlice = createSlice({
  name: 'translation',
  initialState,
  reducers: {
    setEditLanguage: (state, action: PayloadAction<number | null>) => {
      state.languageId = action.payload;
      state.saved = null;
    },
    clearTranslationSaved: state => {
      state.saved = null;
    },
    setLearnerLanguage: (state, action: PayloadAction<string | null>) => {
      state.learnerLanguage = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getRequest.fulfilled, (state, action) => {
        if ((action.meta.arg as IGet).type === EntityTypes.LANG) state.languages = action.payload ?? [];
      })
      .addMatcher(isAnyOf(createRequest.fulfilled, updateRequest.fulfilled), (state, action) => {
        const translation = action.payload as ITranslation;

        // Only a save that wrote a translation — removing one needs no confirmation.
        if (action.meta.arg.type !== EntityTypes.TRANSLATION || !translation?.id || translation.status === -1) return;

        state.saved = { textId: translation.textId, languageId: translation.languageId, at: Date.now() };
      });
  },
});

export const { setEditLanguage, clearTranslationSaved, setLearnerLanguage } = translationSlice.actions;

export default translationSlice.reducer;
